import { useEffect, useState, useRef } from "react";
import SpeechToText from "../utils/SpeechToText";
import { storage as localStorage } from "../utils/storage";

const useMicrophoneAudio = (
  isTranscriptionActive: boolean,
  handleTranscription: (data: { final: boolean; transcript: string }) => void
) => {
  const speechToText = useRef<SpeechToText>(new SpeechToText());
  const [inputVolume, setInputVolume] = useState(0); // State for actual input volume
  const [sliderVolume, setSliderVolume] = useState(1); // State for slider volume
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>("");
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [gainNode, setGainNode] = useState<GainNode | null>(null);
  const [permissionError, setPermissionError] = useState<string>("");

  useEffect(() => {
    const initRecognition = async () => {
      if (isTranscriptionActive) {
        const token = (await localStorage.get("accessToken")) || "";
        speechToText.current.startRecognition(token, handleTranscription);
      } else {
        speechToText.current.endRecognition();
      }
    };

    initRecognition();

    return () => {
      speechToText.current.endRecognition();
    };
  }, [isTranscriptionActive]);

  const requestMicrophonePermission = async () => {
    try {
      // Ensure AudioContext is resumed (iOS suspends it on inactivity)
      if (audioContext && audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Request permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop the temporary stream to prevent background audio issues
      stream.getTracks().forEach((track) => track.stop());

      // Set restart flag when permission is newly granted
      return true;
    } catch (error: any) {
      setPermissionError(error.message || "Microphone permission denied");
      console.error("Error requesting microphone permission:", error);
      return false;
    }
  };

  const getMicrophoneInput = async () => {
    try {
      // Request permission first
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) {
        return;
      }

      // Fetch available microphone devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(
        (device) =>
          device.kind === "audioinput" &&
          device.deviceId !== "default" &&
          device.deviceId !== "communications"
      );
      setMicDevices(audioDevices);

      // Default to the first microphone if none selected
      if (audioDevices.length > 0 && !selectedMic) {
        setSelectedMic(audioDevices[0].deviceId);
      }

      // Get user media stream for the selected microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: selectedMic },
          sampleRate: 44000,
          channelCount: 1,
        },
      });

      const context = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 44000,
      });
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;

      const source = context.createMediaStreamSource(stream);
      const gain = context.createGain();
      gain.gain.value = sliderVolume; // Set initial volume from slider

      source.connect(gain);
      gain.connect(analyser);

      const processor = context.createScriptProcessor(4096, 1, 1);
      gain.connect(processor); // Connect gain to processor instead of source
      processor.connect(context.destination);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer.getChannelData(0);
        const audioChunk = new Int16Array(inputBuffer.length);

        // Convert Float32 to Int16
        for (let i = 0; i < inputBuffer.length; i++) {
          audioChunk[i] = Math.max(-1, Math.min(1, inputBuffer[i])) * 0x7fff;
        }

        // Send the Int16Array directly to the WebSocket
        speechToText.current.writeToStream(audioChunk);

        // Update volume visualization
        analyser.getByteFrequencyData(dataArray);
        const averageVolume =
          dataArray.reduce((a, b) => a + b) / dataArray.length;
        setInputVolume(
          Math.min(100, Math.max(0, Math.round((averageVolume / 127) * 100)))
        );
      };

      setAudioContext(context);
      setGainNode(gain);
    } catch (error: any) {
      console.error("Error accessing microphone:", error);
      setPermissionError(error.message || "Error accessing microphone");
    }
  };

  useEffect(() => {
    getMicrophoneInput();

    const handleDeviceChange = () => {
      getMicrophoneInput();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [selectedMic]);

  const setVolumeLevel = (value: number) => {
    if (gainNode) {
      gainNode.gain.value = value;
      setSliderVolume(value);
    }
  };

  return {
    inputVolume,
    sliderVolume,
    setVolume: setVolumeLevel,
    micDevices,
    selectedMic,
    setSelectedMic,
    permissionError,
    startRecognition: speechToText.current.startRecognition,
    endRecognition: speechToText.current.endRecognition,
  };
};

export default useMicrophoneAudio;
